import styled from "styled-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { identificationFormValidationSchema } from "./IdentificationFormValidationSchema";
import { useHistory, useParams } from "react-router-dom";
import FirstStep from "./steps/FirstStep";
import SecondStep from "./steps/SecondStep";
import {
  IdentificationDocumentData,
  LandingPageProxyPassportFormPassenger,
} from "../../types/IdentificationForm";
import { IdType } from "../../enums/IdType";
import { IdentificationFormStep } from "../../enums/IdentificationFormPath";
import { getIdentificationFormPassengers } from "../../api/identificationFormActions";
import { Breakpoint } from "../../enums/Breakpoints";
import PassportSuccess from "../../icons/PassportSuccess";
import PassportError from "../../icons/PassportError";
import { useTranslationV3 } from "../../i18nV3/useTranslationV3";
import PageLoader from "../../components/PageLoader";
import withV3TranslationsProvider from "../../i18nV3/WithV3TranslationsProvider";

type FormikValues = IdentificationDocumentData[];

const IdentificationForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorSource, setErrorSource] = useState<
    "generic" | "410" | undefined
  >();
  const [passengers, setPassengers] = useState<
    LandingPageProxyPassportFormPassenger[] | undefined
  >();
  const [wasFormSuccessfullySent, setWasFormSuccessfullySent] = useState(false);
  const params = useParams<{ tripId: string }>();
  const history = useHistory();
  const { t } = useTranslationV3();

  const initialData = useMemo(() => {
    if (!passengers) return;
    return passengers.reduce((acc, curr) => {
      acc.push({
        passengerId: curr.id,
        idType: IdType.Passport,
        dateOfExpiry: "--",
        idNumber: "",
        issueCountry: "",
      });
      return acc;
    }, [] as FormikValues);
  }, [passengers]);

  const stepName = useMemo(() => {
    const splitted = history.location.pathname.split("/");
    return splitted[splitted.length - 1];
  }, [history.location]);

  const isFirstStep = useMemo(() => {
    return stepName === IdentificationFormStep.FillData && !errorSource;
  }, [stepName, errorSource]);

  const isSecondStep = useMemo(() => {
    return stepName === IdentificationFormStep.CheckData && !errorSource;
  }, [stepName, errorSource]);

  const isThirdStep = useMemo(() => {
    return stepName === IdentificationFormStep.Confirmation && !errorSource;
  }, [stepName, errorSource]);

  const {
    setFieldValue,
    setValues,
    setFieldError,
    values,
    handleSubmit,
    errors,
  } = useFormik<FormikValues>({
    initialValues: [],
    validationSchema: identificationFormValidationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: (values) => {
      history.push(
        `/identification-form/${params.tripId}/${IdentificationFormStep.CheckData}`
      );
    },
  });

  useEffect(() => {
    if (stepName !== IdentificationFormStep.FillData && !passengers) {
      history.push(
        `/identification-form/${params.tripId}/${IdentificationFormStep.FillData}`
      );
    }
  }, [stepName, history, params, passengers]);

  useEffect(() => {
    if (!!initialData) {
      setValues(initialData);
    }
  }, [setValues, initialData]);

  const getPassengersData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getIdentificationFormPassengers(params.tripId);
      data.length > 0 ? setPassengers(data) : setErrorSource("generic");
    } catch (error: any) {
      error.response?.status === 410
        ? setErrorSource("410")
        : setErrorSource("generic");
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  useEffect(() => {
    if (isFirstStep && !passengers) {
      getPassengersData();
    }
  }, [isFirstStep, passengers, getPassengersData]);

  useEffect(() => {
    if (wasFormSuccessfullySent && !isThirdStep) {
      history.push(
        `/identification-form/${params.tripId}/${IdentificationFormStep.Confirmation}`
      );
    }
  }, [wasFormSuccessfullySent, history, params, isThirdStep]);

  const setValue = useCallback(
    (field: string, value: any) => {
      setFieldValue(field, value);
      setFieldError(field, undefined);
    },
    [setFieldValue, setFieldError]
  );

  const mainTitleKey = useMemo(() => {
    if (isFirstStep) return "tix_ui_identification_form_step_1_title";
    if (isSecondStep) return "tix_ui_identification_form_step_2_title";
    if (isThirdStep) return "tix_ui_identification_form_step_3_title";
    if (errorSource === "410")
      return "tix_ui_identification_form_already_done_title";
    if (errorSource === "generic")
      return "tix_ui_identification_form_generic_error_title";
  }, [isFirstStep, isSecondStep, isThirdStep, errorSource]);

  const subtitleKey = useMemo(() => {
    if (isFirstStep) return "tix_ui_identification_form_step_1_subtitle";
    if (isSecondStep) return "tix_ui_identification_form_step_2_subtitle";
    if (isThirdStep) return "tix_ui_identification_form_step_3_subtitle";
    if (errorSource === "410")
      return "tix_ui_identification_form_already_done_subtitle";
    if (errorSource === "generic")
      return "tix_ui_identification_form_generic_error_subtitle";
  }, [isFirstStep, isSecondStep, isThirdStep, errorSource]);

  return (
    <>
      {isLoading ? (
        <PageLoader />
      ) : (
        <Background>
          <MainContainer>
            <MainTitle>{t(mainTitleKey!)}</MainTitle>
            <Subtitle>{t(subtitleKey!)}</Subtitle>
            {!errorSource && !!passengers && (
              <>
                {isFirstStep && (
                  <FirstStep
                    passengers={passengers}
                    setValue={setValue}
                    errors={errors}
                    values={values}
                    handleSubmit={handleSubmit}
                  />
                )}
                {isSecondStep && (
                  <SecondStep
                    passengers={passengers}
                    values={values}
                    setErrorSource={setErrorSource}
                    setFormSuccessfullySent={setWasFormSuccessfullySent}
                  />
                )}
                {isThirdStep && (
                  <LastStepContainer>
                    <PassportSuccess />
                  </LastStepContainer>
                )}
              </>
            )}
            {!!errorSource && (
              <LastStepContainer>
                <PassportError />
              </LastStepContainer>
            )}
          </MainContainer>
        </Background>
      )}
    </>
  );
};

const LastStepContainer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.redesign.defaultColor};
  padding-top: 40px;

  @media ${Breakpoint.MaxMobile} {
    svg {
      width: 52px;
      height: 75px;
    }
  }
`;

const Background = styled.div`
  background-color: ${({ theme }) => theme.redesign.lightBackground};
  flex-grow: 1;
`;

const MainContainer = styled.div`
  font-family: ${({ theme }) => theme.redesign.regularFont};
  margin: 100px auto;
  width: 728px;

  @media ${Breakpoint.MaxMobile} {
    width: calc(100% - 40px);
    margin: 80px 20px;
  }
`;

const MainTitle = styled.div`
  line-height: 32px;
  padding-bottom: 6px;
  font-size: 32px;
  font-family: ${({ theme }) => theme.redesign.heavyFont};
`;

const Subtitle = styled.div`
  font-size: 18px;
  line-height: 21px;
  margin-bottom: 40px;

  @media ${Breakpoint.MaxMobile} {
    font-size: 14px;
  }
`;
export default withV3TranslationsProvider(IdentificationForm);
